<template>
  <ModuleBase
    :loading="loadingVenue || loadingContract"
    gray
    :class="$style.base"
    @event="taskUpdated"
  >
    <BaseModalSmall
      v-if="showSubscriptionConfirmation"
      @close="showSubscriptionConfirmation = false"
    >
      <BaseText size="l">
        {{ $t('treatwell.landing.no_subscription.message', brandName()) }}
      </BaseText>
      <template #footer>
        <BaseButton
          color="inverted"
          @click="showSubscriptionConfirmation = false"
          v-test="'btn-tw-subscription-cancel'"
        >
          {{ $t('global.actions.cancel') }}
        </BaseButton>
        <BaseButton
          :routerLink="{ name: 'subscription' }"
          @click="onSubscriptionClick"
          v-test="'btn-tw-subscription-submit'"
        >
          {{ $t('treatwell.landing.no_subscription.button') }}
        </BaseButton>
      </template>
    </BaseModalSmall>
    <KYCUltimatumModal
      v-if="showKYCModal"
      @close="showKYCModal = false"
    />
    <KYCStartModal
      v-if="showKYCStartModal"
      @learnMore="goToKYCHelpArticle"
      @close="showKYCStartModal = false"
    />
    <MarketplaceDashboard
      v-model:section="section"
      :treatwellVenue="treatwellVenue"
      :contractDownloadUrl="contractTemplate?.downloadUrl"
      :enableSection="!!enableSection"
      :updated="updated"
      @kyc="showKYCStartModal = true"
      @learnMore="goToKYCHelpArticle"
      @subscribe="showSubscriptionConfirmation = true"
      @cancel="showCancelModal"
    />
    <UltimatumModal />
  </ModuleBase>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { brandName } from '@/helpers/brand-name';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { modal } from '@/helpers/ui';
import ModuleBase from '@/modules/ModuleBase.vue';
import { useCompanyStore } from '@/stores/company';
import { useTreatwellStore } from '@/stores/treatwell';
import type { ContractTemplate, TreatwellVenue } from '@/types';
import MarketplaceDashboard from './dashboard/index.vue';
import type { DashboardSection } from './dashboard/types';
import {
  GET_CONTRACT_TEMPLATE,
  GET_TREATWELL_VENUE,
  TREATWELL_INTEGRATION_REQUEST_REJECT,
} from './graphql';
import KYCStartModal from './modals/KYCStartModal.vue';
import KYCUltimatumModal from './modals/KYCUltimatumModal.vue';
import UltimatumModal from './modals/UltimatumModal.vue';

defineOptions({
  name: 'TreatwellDashboard',
});

const { treatwellStatus, kycUtimatumActive, providedCompanyInfo } = storeToRefs(
  useTreatwellStore(),
);
const { getTreatwellStatus } = useTreatwellStore();
const { isTreatwellUser } = useCompanyStore();

const router = useRouter();

const contractTemplate = ref<ContractTemplate | null>(null);

const { loading: loadingContract, onResult } = useQuery(
  GET_CONTRACT_TEMPLATE,
  {
    type: 'treatwell',
    id: treatwellStatus.value.contractId,
  },
  { enabled: !isTreatwellUser },
);

const showKYCStartModal = ref(false);

// The open page of the dashboard (Dashboard, Profile or Reviews)
const section = ref<DashboardSection>('dashboard');

// Saved in a modal, but not on the marketplace yet
const updated = reactive({
  photos: false,
  description: false,
  roster: false,
  employeeAvailability: false,
});
const taskUpdated = (task: string) => {
  if (task === 'provide_photos') {
    updated.photos = true;
  } else if (task === 'provide_description') {
    updated.description = true;
  } else if (task === 'set_roster') {
    updated.roster = true;
  } else if (task === 'set_employee_availability') {
    updated.employeeAvailability = true;
  }
};

const goToKYCHelpArticle = () => {
  window.open(helpCenterArticleUrl(9863280));
};

const route = useRoute();
const showKYCModal = ref(
  route.name !== 'treatwell-kyc-start' && kycUtimatumActive.value,
);

onResult(({ data }) => {
  contractTemplate.value = data.contractTemplate;
});

const treatwellVenue = ref<Partial<TreatwellVenue>>({});
const {
  loading: loadingVenue,
  onResult: onVenueResult,
  refetch: fetchVenue,
} = useQuery(GET_TREATWELL_VENUE);

onVenueResult(({ data }) => {
  treatwellVenue.value = data.treatwellVenue;
});

const enableSection = computed(
  () =>
    (treatwellStatus.value.contractSigned &&
      providedCompanyInfo.value &&
      treatwellStatus.value.bankDetailsProvided &&
      treatwellStatus.value.kycStatus?.kycCompleted) ||
    treatwellStatus.value.hasVenue,
);

const showSubscriptionConfirmation = ref(false);

const onSubscriptionClick = () => {
  showSubscriptionConfirmation.value = false;
};

const { t } = useI18n();
const { mutate: treatwellIntegrationRequestReject } = useMutation(
  TREATWELL_INTEGRATION_REQUEST_REJECT,
  () => ({
    variables: {
      input: {
        rejectionReason: 'churned from dashboard',
      },
    },
  }),
);

const showCancelModal = () => {
  modal('confirmation', {
    message: t('treatwell.churn_modal.message'),
    type: 'disable',
    warning: t('treatwell.churn_modal.sub_message'),
  }).then(() => {
    treatwellIntegrationRequestReject().then(() => {
      getTreatwellStatus();
      router.push({ name: 'treatwell-landing' });
    });
  });
};

watch(treatwellStatus.value, (curVal) => {
  if (!curVal.hasRequest) {
    router.push({ name: 'treatwell-landing' });
  }
  if (curVal.hasPhotos) {
    fetchVenue();
  }
});
</script>

<style
  lang="scss"
  module
>
.base {
  border-top: 1px solid var(--color-border);
}
</style>
